import React, { memo } from 'react';
import { getProviderNames } from '../../utils/providerUtils';
import { resolveApiImageUrl } from '../../utils/logoUtils';
import { resolveCardGradColor } from '../../utils/cardGradient';
import './ProviderGrid.css';

/**
 * 루미나 화면 층 신작 — 블랙 `components/common/ProviderGrid.jsx` 의 로직 그대로, JSX·CSS 만 교체(시안 블록 10 `sl-` 게임사 카드).
 * WHY: SPEC §2 — 코드·이름 판정, 캐릭터 경로(관리자 표시 금지·업로드 webp 규칙), 로고 위치·크기 변수 주입, 고정 랜덤색은 원본 그대로.
 *      카드는 시안 14/19 네이비 카드: 상단(태그 + 게임 수 또는 LIVE) · 캐릭터 · 하단(한글 이름·영문 코드 · 화살표).
 *      로고는 관리자 위치·크기 설정(--vlogo-*)을 그대로 받아 캐릭터 영역 위에 얹는다(미설정 시 오른쪽 아래 작은 배지).
 *
 * 게임 제공사(벤더) 카드를 그리드 형태로 표시하는 재사용 가능한 컴포넌트 — HomePage, SlotPage, CasinoPage 에서 공통 사용
 *
 * @param {Array} providers - 표시할 제공사 목록
 * @param {string} type - 'slot' 또는 'casino' (캐릭터 이미지 경로 생성에 사용)
 *                        [2026-10-10 미니게임] 'minigame' — 미니게임 목록 화면(pages/MinigamePage.jsx)이 같은 카드를 쓴다(위 태그만 '미니게임')
 * @param {Function} onProviderClick - 제공사 클릭 시 호출될 콜백 함수
 * @param {Object} vendorNames - 벤더 한글 이름 매핑 (선택적, CasinoPage에서 사용)
 */
const ProviderGrid = ({ providers, type, onProviderClick, vendorNames = {} }) => {
  return (
    <div className="provider-grid-section">
      <div className="sl-grid provider-grid">
        {providers.map((provider, index) => {
          // 내부 식별용 코드 (API 호출, 이미지 경로 등에 사용)
          const providerCode = provider.provider ||
                              provider.code ||
                              provider.provider_name ||
                              provider.name ||
                              provider.id ||
                              provider.vendor ||
                              provider.category_name ||
                              'Unknown Provider';

          // 표시용 이름 (display_name, display_name_ko 우선 사용)
          const englishName = provider.name || provider.provider_name || providerCode;
          const lowerProviderCode = providerCode.toLowerCase();
          const koreanName = provider.name_ko ||
            (vendorNames && vendorNames[lowerProviderCode]) ||
            getProviderNames(providerCode).korean;

          // 캐릭터 이미지 경로: API에서 제공한 character_url 사용, 없으면 기본 경로 생성
          const baseProviderName = providerCode.replace(/_casino|_slot/gi, '').toLowerCase();
          /* [2026-08-16] 관리자가 '제거'한 캐릭터는 여기서 경로를 만들지 않는다.
             WHY: character_url 이 비면 아래처럼 게임사 이름으로 경로를 스스로 조립해 결국 그림이 다시 나왔다.
             그래서 서버가 보내는 표시 금지 신호(character_hidden)를 먼저 보고, 참이면 캐릭터를 아예 렌더하지 않는다. */
          const characterHidden = provider.character_hidden === true;
          const characterImagePathPng = characterHidden
            ? null
            : (provider.character_url
              ? resolveApiImageUrl(provider.character_url)
              : `/api/providers/characters/${type}-${baseProviderName}.png`);
          // [2026-06-19 FIX] 관리자 업로드 캐릭터(provider.character_url = /uploads/*.png)는 동명 webp 파일이 없어
          //   아래 <picture>의 webp <source>가 404가 되고 <img> png로 폴백되지 않아 유저페이지에 안 보였다.
          //   업로드 캐릭터는 webp source를 만들지 않고 png <img>만 렌더(로고와 동일). 정적 기본 캐릭터(webp 실존)만 webp 사용.
          const characterImagePathWebp = (characterHidden || (provider.character_url && /\/uploads\//.test(provider.character_url))) ? null : characterImagePathPng.replace(/\.png$/i, '.webp');

          // [2026-06-10 관리자 로고 위치·크기] 관리자가 template_id 기준으로 지정한 로고 배치값을
          //   .provider-logo 에 CSS 커스텀 프로퍼티로만 주입한다(인라인 position 직접 주입 금지).
          //   값이 없으면(미설정) 변수 자체를 만들지 않아 배지 CSS var() fallback 이 그대로 적용된다.
          const hasLogoPos = provider.logo_pos_x != null && provider.logo_pos_y != null;
          const hasLogoSize = provider.logo_w || provider.logo_h;
          const logoVars = {};
          if (hasLogoPos) {
            logoVars['--vlogo-pos'] = 'absolute';
            logoVars['--vlogo-x'] = provider.logo_pos_x + '%';
            logoVars['--vlogo-y'] = provider.logo_pos_y + '%';
            logoVars['--vlogo-transform'] = 'translate(-50%, -50%)';
            // 배지(우하단 고정) 디자인에서 위치 설정 시 right/bottom 을 풀어 left/top 이 우선되게 한다.
            logoVars['--vlogo-right'] = 'auto';
            logoVars['--vlogo-bottom'] = 'auto';
          }
          if (hasLogoSize) {
            if (provider.logo_w) {
              logoVars['--vlogo-w'] = provider.logo_w === 'auto' ? 'auto' : provider.logo_w + 'px';
              // 너비를 숫자로 설정하면 배지의 기존 max-width(45%) 제한을 풀어 의도한 크기가 보장되게 한다.
              // [2026-09-30] '자동'은 숫자 설정이 아니다 — 높이에 맞춘 비율 너비라 긴 로고(마이크로게이밍·분고 등)가
              //   카드보다 넓어져 양옆이 잘렸다(루미나 기본값 높이 56·너비 자동, 실측). 자동이면 카드 안(좌우 12px 여백)까지로 묶는다.
              logoVars['--vlogo-maxw'] = provider.logo_w === 'auto' ? 'calc(100% - 24px)' : 'none';
            }
            if (provider.logo_h) logoVars['--vlogo-h'] = provider.logo_h === 'auto' ? 'auto' : provider.logo_h + 'px';
          }

          // [2026-06-11 Phase 5] 게임사별 고정 랜덤색 — type(슬롯/카지노)별 팔레트에서 providerCode 해시로 결정.
          //   랜덤 미설정이면 null → 변수 미주입 → var(--card-grad-color,…) fallback(고정색) 적용(무손상).
          const cardGradColor = resolveCardGradColor(type, providerCode);
          const itemStyle = cardGradColor ? { '--card-grad-color': cardGradColor } : undefined;

          // [루미나] 게임 수 = 응답값(game_count). 없으면 칸을 비운다(지어내지 않음)
          const gameCount = provider.game_count != null && provider.game_count !== '' && !isNaN(Number(provider.game_count))
            ? Number(provider.game_count).toLocaleString('ko-KR')
            : null;

          const activate = () => onProviderClick(provider);

          return (
            <article
              key={`provider-${providerCode}-${index}`}
              className="sl-card provider-item"
              style={itemStyle}
              role="button"
              tabIndex={0}
              onClick={activate}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); } }}
            >
              {/* [2026-09-30] 그림 층 — 카드 전체를 채운다(카드 = 그림 비율). 로고는 아래 .sl-im(위·아래 줄 사이 칸)에 남겨
                  관리자가 정한 로고 위치(%)가 종전처럼 그 칸 기준으로 잡히게 한다 — 그림과 같은 칸에 두면 로고가 이름 줄 위로 내려앉았다(1440 실측). */}
              <div className="sl-art">
                {/* [2026-08-16] 표시 금지면 캐릭터 영역 자체를 그리지 않는다(빈 그림 요청·깨진 아이콘 방지) */}
                {!characterHidden && (
                <picture>
                  {characterImagePathWebp && <source srcSet={characterImagePathWebp} type="image/webp" />}
                  <img
                    className="provider-character"
                    src={characterImagePathPng}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      // picture 태그 전체를 숨김
                      e.target.closest('picture').style.display = 'none';
                    }}
                  />
                </picture>
                )}
              </div>
              <div className="sl-top">
                <span className="tag">{type === 'slot' ? '슬롯' : type === 'minigame' ? '미니게임' : '카지노'}</span>
                {type === 'slot'
                  ? (gameCount && <span className="sl-cnt">{gameCount} 게임</span>)
                  : <span className="cs-live">LIVE</span>}
              </div>
              <div className="sl-im">
                {/* [2026-08-16] 로고도 표시 금지면 그리지 않는다. 이름·코드 글자는 하단 줄에 항상 있어 어느 게임사인지 계속 식별 가능. */}
                {provider.logo_hidden !== true && (
                <img
                  className="provider-logo"
                  src={resolveApiImageUrl(provider.logo_url)}
                  alt={englishName}
                  loading="lazy"
                  style={Object.keys(logoVars).length ? logoVars : undefined}
                  onLoad={(e) => {
                    // 실제 게임사 로고가 표시된 카드만 표식(has-real-logo). 기본 로고(default-logo.svg) 폴백·로드 실패 카드는 표식을 달지 않는다.
                    if (!e.target.dataset.errorHandled && !e.target.src.includes('default-logo.svg')) {
                      const card = e.target.closest('.provider-item');
                      if (card) card.classList.add('has-real-logo');
                    }
                  }}
                  onError={(e) => {
                    if (!e.target.dataset.errorHandled) {
                      e.target.dataset.errorHandled = 'true';
                      if (!e.target.src.includes('default-logo.svg')) {
                        e.target.src = '/img/default-logo.svg';
                      } else {
                        e.target.style.display = 'none';
                      }
                    }
                  }}
                />
                )}
              </div>
              <div className="sl-bottom">
                <div className="sl-txt provider-name">
                  <div className="sl-name provider-korean">{koreanName}</div>
                  <div className="sl-code provider-english">{englishName}</div>
                </div>
                <span className="sl-go" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                </span>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default memo(ProviderGrid);
